import { useState, useMemo, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Company } from '@/types/company';
import type { CallRecord } from '@/types/callRecord';
import {
  callStatusOptions, feedbackOptions, callStatusLabels, callFeedbackLabels,
} from '@/types/callRecord';
import { fetchCompanies } from '@/api/companies';
import {
  fetchCallRecords, createCallRecord, fetchCallRecordStats, fetchTodayFollowUps, fetchCallRecord,
  type CallRecordStats, type CreateCallRecordPayload,
} from '@/api/callRecords';
import Layout from '@/components/feature/Layout';
import AddCallRecordModal from '@/pages/telemarketing/components/AddCallRecordModal';

interface CallPreset {
  companyId: string;
  companyName: string;
  contactId: string;
  contactName: string;
  contactPhone: string;
}

export default function TelemarketingPage() {
  const [callRecords, setCallRecords] = useState<CallRecord[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [stats, setStats] = useState<CallRecordStats>({ total: 0, todayCount: 0, connected: 0, interested: 0, needsCallback: 0 });
  const [todayFollowUps, setTodayFollowUps] = useState<CallRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'queue' | 'history'>('queue');
  const [showAddModal, setShowAddModal] = useState(false);
  const [callPreset, setCallPreset] = useState<CallPreset | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('全部');
  const [feedbackFilter, setFeedbackFilter] = useState<string>('全部');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 8;
  const [followUpExpanded, setFollowUpExpanded] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const followUpId = searchParams.get('followUpId');
    if (!followUpId) return;
    fetchCallRecord(followUpId)
      .then((record) => {
        setCallPreset({
          companyId: record.companyId,
          companyName: record.companyName,
          contactId: record.contactId,
          contactName: record.contactName,
          contactPhone: record.contactPhone,
        });
        setShowAddModal(true);
      })
      .catch(() => {
        // 记录不存在或已被删除,静默忽略,销售自己手动新建
      })
      .finally(() => {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete('followUpId');
          return next;
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load call records
  const loadCallRecords = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchCallRecords({
        search: searchQuery || undefined,
        status: statusFilter !== '全部' ? statusFilter : undefined,
        feedback: feedbackFilter !== '全部' ? feedbackFilter : undefined,
        page: currentPage,
        pageSize,
      });
      setCallRecords(result.data);
      setTotalRecords(result.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载通话记录失败');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, statusFilter, feedbackFilter, currentPage]);

  // Load stats
  const loadStats = useCallback(async () => {
    try {
      const [statsData, followUpsData] = await Promise.all([
        fetchCallRecordStats(),
        fetchTodayFollowUps(),
      ]);
      setStats(statsData);
      setTodayFollowUps(followUpsData);
    } catch {
      // non-critical
    }
  }, []);

  // Load companies for queue
  const loadQueueData = useCallback(async () => {
    try {
      const result = await fetchCompanies({ page: 1, pageSize: 100 });
      setCompanies(result.data);
    } catch {
      // non-critical
    }
  }, []);

  useEffect(() => {
    loadCallRecords();
  }, [loadCallRecords]);

  useEffect(() => {
    loadStats();
    loadQueueData();
  }, [loadStats, loadQueueData]);

  // Stats display
  const displayStats = useMemo(() => stats, [stats]);

  // Filtered records (server-side, local for display)
  const filteredRecords = callRecords;

  // Pagination
  const pagedRecords = filteredRecords;

  // Call queue
  const callQueue = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

    return companies.map((company: Company) => {
      const companyRecords = callRecords.filter((r) => r.companyId === company.id);
      const lastCallDate = companyRecords.length > 0
        ? companyRecords.reduce((latest, r) => (r.callDate > latest ? r.callDate : latest), '')
        : null;

      const contactsWithStatus = company.contacts.map((contact) => {
        const contactRecords = companyRecords.filter((r) => r.contactId === contact.id);
        const lastRecord = contactRecords.length > 0
          ? contactRecords.reduce((latest, r) => (r.createdAt > latest ? r.createdAt : latest), contactRecords[0].createdAt)
          : null;
        const lastRecordObj = contactRecords.find((r) => r.createdAt === lastRecord) || null;
        const needsCallback = lastRecordObj?.status === '需回拨' || (lastRecordObj?.nextFollowUp && lastRecordObj.nextFollowUp <= today);

        return {
          ...contact,
          lastRecord: lastRecordObj,
          hasRecentCall: !!lastRecord && lastRecord > sevenDaysAgo,
          needsCallback,
        };
      });

      return {
        ...company,
        lastCallDate,
        contactsWithStatus,
        totalCalls: companyRecords.length,
        priority: contactsWithStatus.some((c) => c.needsCallback) ? 0 :
                  !lastCallDate || lastCallDate < sevenDaysAgo ? 1 : 2,
      };
    })
    .filter((c) => c.contactsWithStatus.length > 0)
    .sort((a, b) => a.priority - b.priority);
  }, [callRecords, companies]);

  const [queueSearch, setQueueSearch] = useState('');

  const filteredQueue = useMemo(() => {
    if (!queueSearch.trim()) return callQueue;
    const q = queueSearch.toLowerCase();
    return callQueue.filter((c) => {
      const matchName = c.name.toLowerCase().includes(q);
      const matchContact = c.contactsWithStatus.some(
        (ct) => ct.name.toLowerCase().includes(q) || ct.phone.includes(q),
      );
      const matchPerson = c.responsiblePerson.toLowerCase().includes(q);
      return matchName || matchContact || matchPerson;
    });
  }, [callQueue, queueSearch]);

  const handleAddRecord = async (data: Omit<CallRecord, 'id' | 'createdAt'>) => {
    try {
      await createCallRecord(data as CreateCallRecordPayload);
      await Promise.all([loadCallRecords(), loadStats()]);
    } catch {
      // silently fail
    }
  };

  const openCallModal = (company: Company, contact?: typeof company.contacts[0]) => {
    setCallPreset({
      companyId: company.id,
      companyName: company.name,
      contactId: contact?.id || '',
      contactName: contact?.name || '',
      contactPhone: contact?.phone || '',
    });
    setShowAddModal(true);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('全部');
    setFeedbackFilter('全部');
    setCurrentPage(1);
  };

  const hasFilters = searchQuery || statusFilter !== '全部' || feedbackFilter !== '全部';

  return (
    <Layout>
      <div className="p-4 md:p-6 space-y-5">
        {/* Page header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">电销中心</h1>
            <p className="text-sm text-foreground-500 mt-1">外呼管理、通话记录与客户反馈追踪</p>
          </div>
          <button
            onClick={() => {
              setCallPreset(null);
              setShowAddModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2.5 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-phone-line text-base"></i>
            新增通话记录
          </button>
        </div>

        {/* Error banner */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <i className="ri-error-warning-line text-red-500"></i>
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setError(null); loadCallRecords(); loadStats(); }}
                className="px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                重试
              </button>
              <button
                onClick={() => setError(null)}
                className="w-6 h-6 flex items-center justify-center text-red-400 hover:text-red-600 cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
          </div>
        )}

        {/* Today follow-up reminders */}
        {todayFollowUps.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 cursor-pointer" onClick={() => setFollowUpExpanded(!followUpExpanded)}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-200 flex items-center justify-center flex-shrink-0">
                  <i className="ri-alarm-warning-line text-lg text-amber-600"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-amber-800">
                    今日待跟进提醒
                    <span className="ml-2 inline-flex items-center justify-center w-5 h-5 bg-amber-500 text-background-50 rounded-full text-[11px] font-bold">
                      {todayFollowUps.length}
                    </span>
                  </p>
                  <p className="text-xs text-amber-600 mt-0.5">
                    {todayFollowUps.length} 条通话记录需要在今天跟进，涉及 {new Set(todayFollowUps.map((r) => r.companyName)).size} 家企业
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <i className={`${followUpExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-amber-500`}></i>
              </div>
            </div>
            {followUpExpanded && (
              <div className="border-t border-amber-200 divide-y divide-amber-100">
                {todayFollowUps.map((record) => (
                  <div key={record.id} className="flex items-center justify-between px-4 py-3 hover:bg-amber-100/50 transition-colors">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-medium text-amber-700">
                          {record.contactName.charAt(0)}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground-800 truncate">
                          {record.contactName}
                          <span className="text-foreground-500 font-normal ml-1.5 text-xs">{record.companyName}</span>
                        </p>
                        <p className="text-xs text-foreground-500 mt-0.5 line-clamp-1">{record.notes}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium ${record.feedback ? callFeedbackLabels[record.feedback]?.color || 'bg-background-100 text-foreground-500' : 'bg-background-100 text-foreground-500'}`}>
                        {record.feedback || record.status}
                      </span>
                      <span className="text-xs text-foreground-500 whitespace-nowrap">{record.callDate}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <div className="bg-background-50 rounded-xl p-3.5 border border-background-200/70">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-phone-line text-base text-primary-600"></i>
              </div>
              <div>
                <p className="text-xl font-heading font-bold text-foreground-900">{displayStats.total}</p>
                <p className="text-[11px] text-foreground-500">总通话</p>
              </div>
            </div>
          </div>
          <div className="bg-background-50 rounded-xl p-3.5 border border-background-200/70">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-calendar-check-line text-base text-accent-600"></i>
              </div>
              <div>
                <p className="text-xl font-heading font-bold text-foreground-900">{displayStats.todayCount}</p>
                <p className="text-[11px] text-foreground-500">今日通话</p>
              </div>
            </div>
          </div>
          <div className="bg-background-50 rounded-xl p-3.5 border border-background-200/70">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-check-double-line text-base text-green-600"></i>
              </div>
              <div>
                <p className="text-xl font-heading font-bold text-foreground-900">{displayStats.connected}</p>
                <p className="text-[11px] text-foreground-500">已接通</p>
              </div>
            </div>
          </div>
          <div className="bg-background-50 rounded-xl p-3.5 border border-background-200/70">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-star-line text-base text-secondary-600"></i>
              </div>
              <div>
                <p className="text-xl font-heading font-bold text-foreground-900">{displayStats.interested}</p>
                <p className="text-[11px] text-foreground-500">意向客户</p>
              </div>
            </div>
          </div>
          <div className="bg-background-50 rounded-xl p-3.5 border border-background-200/70">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-phone-find-line text-base text-amber-600"></i>
              </div>
              <div>
                <p className="text-xl font-heading font-bold text-foreground-900">{displayStats.needsCallback}</p>
                <p className="text-[11px] text-foreground-500">待回拨</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tab switcher */}
        <div className="flex items-center gap-1 bg-background-100 rounded-xl p-1 w-fit">
          <button
            onClick={() => setActiveTab('queue')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
              activeTab === 'queue'
                ? 'bg-background-50 text-foreground-900'
                : 'text-foreground-500 hover:text-foreground-700'
            }`}
          >
            <i className="ri-contacts-line text-base"></i>
            拨号列表
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
              activeTab === 'history'
                ? 'bg-background-50 text-foreground-900'
                : 'text-foreground-500 hover:text-foreground-700'
            }`}
          >
            <i className="ri-history-line text-base"></i>
            通话记录
            <span className="text-xs text-foreground-400">({totalRecords})</span>
          </button>
        </div>

        {/* Loading state */}
        {loading && activeTab === 'history' && (
          <div className="bg-background-50 rounded-xl border border-background-200/70 py-16 text-center">
            <div className="flex flex-col items-center gap-3">
              <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i>
              <p className="text-sm text-foreground-500">加载通话记录...</p>
            </div>
          </div>
        )}

        {/* Call Queue */}
        {activeTab === 'queue' && (
          <div className="space-y-4">
            {/* Queue search */}
            <div className="bg-background-50 rounded-xl border border-background-200/70 p-4">
              <div className="relative max-w-md">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
                <input
                  type="text"
                  value={queueSearch}
                  onChange={(e) => setQueueSearch(e.target.value)}
                  placeholder="搜索企业、联系人、负责人..."
                  className="w-full h-10 pl-10 pr-4 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all"
                />
              </div>
            </div>

            {/* Queue list */}
            <div className="space-y-3">
              {filteredQueue.map((company) => (
                <div
                  key={company.id}
                  className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden"
                >
                  <div className="flex items-center justify-between px-4 py-3 bg-background-100/50 border-b border-background-200/50">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-building-2-line text-primary-600"></i>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground-800 truncate">{company.name}</p>
                        <p className="text-xs text-foreground-500">
                          {company.type}
                          {company.lastCallDate && (
                            <span className="ml-2">上次联系：{company.lastCallDate}</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {company.priority === 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-600 rounded-md text-[11px] font-medium">
                          <i className="ri-alarm-line text-xs"></i>
                          待回拨
                        </span>
                      )}
                      {company.priority === 1 && !company.lastCallDate && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-accent-50 text-accent-600 rounded-md text-[11px] font-medium">
                          新客户
                        </span>
                      )}
                      <span className="text-xs text-foreground-400">
                        {company.totalCalls > 0 ? `${company.totalCalls}次通话` : '暂无记录'}
                      </span>
                    </div>
                  </div>

                  <div className="divide-y divide-background-200/50">
                    {company.contactsWithStatus.map((contact) => (
                      <div
                        key={contact.id}
                        className="flex items-center justify-between px-4 py-3 hover:bg-background-50 transition-colors"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-8 h-8 rounded-full bg-background-200 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-medium text-foreground-600">
                              {contact.name.charAt(0)}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-foreground-800">{contact.name}</p>
                              <span className="text-xs text-foreground-500">{contact.position}</span>
                            </div>
                            <p className="text-xs text-foreground-500">{contact.phone}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {contact.needsCallback && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-600 rounded-md text-[11px] font-medium">
                              <i className="ri-alarm-line text-xs"></i>
                              需回拨
                            </span>
                          )}
                          {contact.lastRecord && (
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium ${
                                contact.lastRecord.feedback
                                  ? callFeedbackLabels[contact.lastRecord.feedback]?.color || 'bg-background-100 text-foreground-500'
                                  : 'bg-background-100 text-foreground-500'
                              }`}
                            >
                              {contact.lastRecord.feedback || contact.lastRecord.status}
                            </span>
                          )}
                          <button
                            onClick={() => openCallModal(company, {
                              id: contact.id,
                              name: contact.name,
                              phone: contact.phone,
                              position: contact.position,
                            } as typeof company.contacts[0])}
                            className="flex items-center gap-1.5 px-3 py-2 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-phone-line text-sm"></i>
                            拨号
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {filteredQueue.length === 0 && (
                <div className="bg-background-50 rounded-xl border border-background-200/70 py-16 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center">
                      <i className="ri-contacts-line text-xl text-foreground-400"></i>
                    </div>
                    <p className="text-sm text-foreground-500">
                      {queueSearch ? '未找到匹配的企业或联系人' : '所有客户均已联系'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Call History */}
        {activeTab === 'history' && !loading && (
          <div className="space-y-4">
            {/* Filters */}
            <div className="bg-background-50 rounded-xl border border-background-200/70 p-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1 min-w-0">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setCurrentPage(1);
                    }}
                    placeholder="搜索企业、联系人、备注..."
                    className="w-full h-10 pl-10 pr-4 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all"
                  />
                </div>

                <div className="flex items-center gap-1.5 bg-background-100 rounded-lg p-1 flex-shrink-0 overflow-x-auto">
                  <button
                    onClick={() => { setStatusFilter('全部'); setCurrentPage(1); }}
                    className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                      statusFilter === '全部' ? 'bg-background-50 text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    全部状态
                  </button>
                  {callStatusOptions.map((opt) => (
                    <button
                      key={opt}
                      onClick={() => { setStatusFilter(opt); setCurrentPage(1); }}
                      className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                        statusFilter === opt ? 'bg-background-50 text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>

                <div className="flex items-center gap-1.5 bg-background-100 rounded-lg p-1 flex-shrink-0 overflow-x-auto">
                  <button
                    onClick={() => { setFeedbackFilter('全部'); setCurrentPage(1); }}
                    className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                      feedbackFilter === '全部' ? 'bg-background-50 text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    全部反馈
                  </button>
                  {feedbackOptions.map((opt) => (
                    <button
                      key={opt}
                      onClick={() => { setFeedbackFilter(opt); setCurrentPage(1); }}
                      className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                        feedbackFilter === opt ? 'bg-background-50 text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>

                {hasFilters && (
                  <button
                    onClick={clearFilters}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-close-circle-line text-sm"></i>
                    清除
                  </button>
                )}
              </div>
            </div>

            {/* Records table */}
            <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-background-200/70 bg-background-100/50">
                      <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 uppercase tracking-wider whitespace-nowrap">联系对象</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 uppercase tracking-wider hidden sm:table-cell whitespace-nowrap">企业</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 uppercase tracking-wider whitespace-nowrap">通话状态</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 uppercase tracking-wider whitespace-nowrap">反馈</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 uppercase tracking-wider hidden lg:table-cell whitespace-nowrap">沟通备注</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 uppercase tracking-wider whitespace-nowrap">通话时间</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 uppercase tracking-wider hidden md:table-cell whitespace-nowrap">下次跟进</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-background-200/70">
                    {pagedRecords.map((record) => {
                      const statusInfo = callStatusLabels[record.status];
                      const feedbackInfo = record.feedback ? callFeedbackLabels[record.feedback] : null;
                      return (
                        <tr key={record.id} className="hover:bg-background-100/50 transition-colors group">
                          <td className="px-4 py-3.5">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-background-200 flex items-center justify-center flex-shrink-0">
                                <span className="text-xs font-medium text-foreground-600">{record.contactName.charAt(0)}</span>
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-foreground-800">{record.contactName}</p>
                                <p className="text-xs text-foreground-500">{record.contactPhone}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3.5 hidden sm:table-cell">
                            <span className="text-sm text-foreground-700 max-w-[140px] truncate block">{record.companyName}</span>
                          </td>
                          <td className="px-4 py-3.5">
                            <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${statusInfo.color} whitespace-nowrap`}>
                              <i className={`${statusInfo.icon} text-sm`}></i>
                              {record.status}
                            </span>
                          </td>
                          <td className="px-4 py-3.5">
                            {feedbackInfo ? (
                              <span className={`inline-flex px-2 py-0.5 rounded-md text-[11px] font-medium ${feedbackInfo.color} whitespace-nowrap`}>
                                {record.feedback}
                              </span>
                            ) : (
                              <span className="text-xs text-foreground-400">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3.5 hidden lg:table-cell">
                            <div className="relative group/notes">
                              <p className="text-xs text-foreground-600 line-clamp-2 max-w-[220px]">{record.notes}</p>
                              {record.notes.length > 40 && (
                                <div className="absolute bottom-full left-0 mb-1 hidden group-hover/notes:block z-10">
                                  <div className="bg-foreground-900 text-background-50 text-xs rounded-lg px-3 py-2 max-w-[300px] whitespace-pre-wrap">
                                    {record.notes}
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3.5">
                            <div className="whitespace-nowrap">
                              <p className="text-sm text-foreground-700">{record.callDate}</p>
                              <p className="text-xs text-foreground-500">{record.callTime}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3.5 hidden md:table-cell">
                            {record.nextFollowUp ? (
                              <span className={`inline-flex items-center gap-1 text-xs whitespace-nowrap ${
                                record.nextFollowUp < new Date().toISOString().split('T')[0]
                                  ? 'text-amber-600 font-medium'
                                  : 'text-foreground-600'
                              }`}>
                                <i className="ri-calendar-line text-xs"></i>
                                {record.nextFollowUp}
                                {record.nextFollowUp < new Date().toISOString().split('T')[0] && (
                                  <span className="text-[10px] text-red-500">超期</span>
                                )}
                              </span>
                            ) : (
                              <span className="text-xs text-foreground-400">-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {pagedRecords.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-4 py-16 text-center">
                          <div className="flex flex-col items-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center">
                              <i className="ri-history-line text-xl text-foreground-400"></i>
                            </div>
                            <p className="text-sm text-foreground-500">暂无通话记录</p>
                            {hasFilters && (
                              <button
                                onClick={clearFilters}
                                className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer"
                              >
                                清除筛选条件
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {totalRecords > pageSize && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-background-200/70">
                  <p className="text-xs text-foreground-500">
                    共 <strong className="text-foreground-700">{totalRecords}</strong> 条，
                    第 {currentPage}/{Math.ceil(totalRecords / pageSize)} 页
                  </p>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="w-8 h-8 flex items-center justify-center rounded-lg border border-background-200 text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <i className="ri-arrow-left-s-line"></i>
                    </button>
                    {Array.from({ length: Math.min(Math.ceil(totalRecords / pageSize), 7) }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                          currentPage === page
                            ? 'bg-accent-500 text-background-50'
                            : 'border border-background-200 text-foreground-500 hover:bg-background-100'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(Math.ceil(totalRecords / pageSize), p + 1))}
                      disabled={currentPage === Math.ceil(totalRecords / pageSize)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg border border-background-200 text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <i className="ri-arrow-right-s-line"></i>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Add call record modal */}
        <AddCallRecordModal
          isOpen={showAddModal}
          onClose={() => {
            setShowAddModal(false);
            setCallPreset(null);
          }}
          onSubmit={handleAddRecord}
          presetData={callPreset}
        />
      </div>
    </Layout>
  );
}